import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Map, GitBranch, Check, ArrowRight, BarChart3, Layers, MessageSquare, Cpu } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = { Target, Map, GitBranch };
const productServices = services.filter(s => ['Target', 'Map', 'GitBranch'].includes(s.icon));

const SERVICES_LIST = [
  { icon: Target, title: 'Product Marketing', desc: 'Position your product for the right market. We help craft messaging, positioning, and go-to-market strategies that resonate with your ICP.', details: ['Market positioning & messaging', 'Competitive analysis', 'Content strategy for product launches', 'Customer persona development'] },
  { icon: Layers, title: 'Product Relevance & Roadmap', desc: 'Ensure your product stays aligned with market needs and customer demands at every stage of growth.', details: ['Feature prioritization frameworks', 'TAM/SAM/SOM analysis', 'Customer feedback integration', 'Product-market fit validation'] },
  { icon: GitBranch, title: 'Product Roadmap to CTO/CPO', desc: 'Bridge the gap between business goals and technical execution with clear communication and alignment.', details: ['Technical roadmap alignment', 'Business-to-engineering translation', 'Sprint planning & prioritization', 'Cross-functional alignment workshops'] },
  { icon: BarChart3, title: 'Revenue-Driven Product Strategy', desc: 'Align product decisions directly with revenue outcomes, ensuring every feature built contributes to ARR growth.', details: ['Pricing strategy & packaging', 'Upsell/cross-sell opportunity mapping', 'Churn analysis & retention features', 'Revenue attribution to product'] },
  { icon: MessageSquare, title: 'Customer Success Alignment', desc: 'Connect product development with customer success to reduce churn and increase expansion revenue.', details: ['Customer health scoring', 'Onboarding experience optimization', 'Feature adoption tracking', 'Voice of customer programs'] },
  { icon: Cpu, title: 'AI & Innovation Roadmapping', desc: 'Navigate the AI transformation with a product strategy that leverages emerging technologies for competitive advantage.', details: ['AI integration assessment', 'Innovation pipeline design', 'Build vs buy analysis', 'Technology stack evaluation'] },
];

export default function ProductRoadmappingPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="hero-gradient pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="product-roadmapping-title">
            Product Roadmapping
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            Align product strategy with market needs and revenue goals. From marketing to CTO/CPO alignment.
          </p>
        </div>
      </section>

      {/* Existing Services from mock data */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="product-services">
        <div className="max-w-5xl mx-auto space-y-12">
          {productServices.map((service) => {
            const Icon = iconMap[service.icon] || Target;
            return (
              <div key={service.id} className="card-depth rounded-2xl p-8 lg:p-10" data-testid={`product-service-${service.id}`}>
                <div className="flex items-start gap-5 mb-8">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">{service.title}</h2>
                    <p className="text-muted-foreground leading-relaxed text-lg">{service.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-0 lg:ml-[4.75rem]">
                  {service.details.map((detail) => (
                    <div key={detail} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Full Services Section */}
      <section className="py-20 lg:py-28 bg-muted px-4 sm:px-6 lg:px-8" data-testid="product-full-services">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-4">Our Services</h2>
          <p className="text-muted-foreground text-center mb-16 text-lg max-w-2xl mx-auto">
            End-to-end product strategy and roadmapping services to drive growth at every stage.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES_LIST.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.title} className="card-depth rounded-2xl p-7" data-testid={`svc-${service.title.substring(0,10).toLowerCase().replace(/\s/g,'-')}`}>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{service.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.details.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Check className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Product Roadmapping */}
      <section className="py-20 lg:py-28 bg-primary/5 px-4 sm:px-6 lg:px-8" data-testid="product-why">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-16">
            Why Product Roadmapping Matters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Revenue Alignment', desc: 'Ensure every feature you build directly contributes to revenue growth and market expansion.' },
              { title: 'Market Validation', desc: 'Validate product-market fit before investing resources. Build what customers actually need.' },
              { title: 'CTO/CPO Bridge', desc: 'Close the gap between product vision and technical execution with clear communication frameworks.' },
            ].map((item) => (
              <div key={item.title} className="card-depth rounded-2xl p-8 text-center">
                <div className="w-12 h-1 bg-accent rounded-full mx-auto mb-6" />
                <h3 className="text-lg font-bold text-foreground mb-3">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 cta-gradient animate-gradient" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Align your product with your revenue
          </h2>
          <p className="text-lg text-white/75 mb-8 max-w-xl mx-auto">
            Let's build a roadmap that drives growth.
          </p>
          <Link to="/contact" data-testid="product-cta-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group">
            Talk to an Expert <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
